import type { AuthAnswer, Problem, SignIn, TenantAuth } from '@hive/api';
import { ApiError } from '@hive/api';
import { type Lang } from '@hive/i18n';
import { Brand, ErrorBox, LangFlags, Spinner, explainProblem, useToast, useHelpScreen } from '@hive/ui';
import { SignInSteps } from './SignInSteps';
import { useState } from 'react';
import { useI18nP, useT } from '../../i18n';
import { logoSrc } from '../../lib/logo';
import { dismissInstallHint, installHintDismissed, isStandalone, useInstallPrompt } from '../../lib/pwa';
import { rememberLang } from '../../lib/session';

/**
 * T-01 — Entrar. A empresa vem do endereço; só aparecem os métodos que ela ligou
 * (HU-001, HU-023) e nada indica que existam outros tenants. Senha, código do autenticador,
 * convite e redefinição acontecem aqui mesmo (login próprio do Hive, sem redirecionar);
 * só a conta da empresa (OIDC) passa pelo provedor dela.
 */
export function SignInScreen({
  tenant,
  auth,
  problem,
  returnTo,
  onLang,
  pending,
  onDone,
}: {
  tenant: SignIn;
  auth: TenantAuth;
  problem: Problem | null;
  returnTo: string;
  onLang: (l: Lang) => void;
  /** Próximo passo vindo da volta do provedor da empresa (código ou cadastro do autenticador). */
  pending?: AuthAnswer | null;
  /** Entrou: a abertura recarrega a sessão. */
  onDone: () => void;
}) {
  useHelpScreen('t01');
  const t = useT();
  const i18n = useI18nP();
  const { lang } = i18n;
  const toast = useToast();
  const install = useInstallPrompt();
  const [busy, setBusy] = useState<string | null>(null);
  const [showPwa, setShowPwa] = useState(() => !isStandalone() && !installHintDismissed());
  const methods = new Set(tenant.methods ?? []);
  const branding = tenant.branding ?? {};
  const product = typeof branding.productName === 'string' && branding.productName ? branding.productName : 'Hive';
  const logo = logoSrc(tenant.slug, branding.logoUrl);
  const host = window.location.host;

  const go = async (key: string, idpHint?: string) => {
    setBusy(key);
    try {
      await auth.signIn(returnTo, { idpHint, lang });
    } catch {
      setBusy(null);
      toast(t('signin.unreachable'), { kind: 'crit' });
    }
  };

  const explained =
    problem?.code === 'callback'
      ? { what: t('signin.callback.what'), why: t('signin.callback.why'), fix: t('signin.callback.fix') }
      : problem
        ? explainProblem(new ApiError(problem.status ?? 401, problem), i18n)
        : null;

  return (
    <div className="login-wrap">
      <aside className="login-side">
        <div className="stack">
          <Brand productName={product} logoUrl={logo} />
          <p style={{ fontSize: 22, fontFamily: 'var(--font-cond)', fontWeight: 600, maxWidth: '24ch', lineHeight: 1.25 }}>{t('signin.tagline')}</p>
          <ul className="stack-sm muted" style={{ paddingLeft: 18, margin: 0 }}>
            <li>{t('signin.bullet1')}</li>
            <li>{t('signin.bullet2')}</li>
            <li>{t('signin.bullet3')}</li>
          </ul>
        </div>
        <span className="small muted">{t('signin.sideFoot')}</span>
      </aside>
      <main className="login-main" id="main">
        <div className="login-card">
          <div className="row between">
            <span className="muted small mono">{host}</span>
            {logo && <img className="tenant-logo" src={logo} alt="" referrerPolicy="no-referrer" />}
          </div>
          <div className="stack-sm" style={{ gap: 4 }}>
            <h1 tabIndex={-1}>{t('signin.title', { product })}</h1>
            <p className="muted">{tenant.name}</p>
          </div>
          {explained && <ErrorBox e={explained} />}
          {busy ? (
            <div className="stack-sm">
              <Spinner label={t('signin.redirecting')} />
              <p className="muted small">{t('signin.redirectingHint')}</p>
            </div>
          ) : (
            <SignInSteps auth={auth} tenant={tenant} methods={methods} pending={pending} onDone={onDone} onSso={(m) => go(m, m)} />
          )}
          {showPwa && (
            <div className="pwa-banner" role="region" aria-label={t('signin.pwa.title')}>
              <span className="app-icon" aria-hidden="true" />
              <div className="min0" style={{ flex: 1 }}>
                <b>{t('signin.pwa.title')}</b>
                <br />
                <span className="muted small">{install.available ? t('signin.pwa.text') : t('signin.pwa.manual')}</span>
              </div>
              <div className="stack-sm">
                {install.available && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={async () => {
                      const ok = await install.install();
                      if (ok) setShowPwa(false);
                    }}
                  >
                    {t('signin.pwa.install')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    dismissInstallHint();
                    setShowPwa(false);
                    toast(t('signin.pwa.laterToast'), { kind: 'info' });
                  }}
                >
                  {t('signin.pwa.later')}
                </button>
              </div>
            </div>
          )}
          <div className="lang-field">
            <span className="small strong" aria-hidden="true">
              {t('lang.label')}
            </span>
            {/* a escolha fica no navegador e, ao entrar, vale para quem ainda não tem idioma salvo no perfil */}
            <LangFlags
              id="signin-lang"
              label={t('lang.label')}
              value={lang}
              onChange={(l) => {
                rememberLang(l);
                onLang(l);
              }}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
